import type {
  PlayerId,
  WarriorInstance,
  GameAction,
  MoveAction,
  DashAction,
  AttackAction,
  HealAction,
  BoardPosition,
} from '@/types';
import type { BoardState } from '@/types/game.types';
import { BoardManager } from './BoardManager';
import { CombatResolver } from './CombatResolver';

export interface DamageInfo {
  targetId: string;
  damage: number;
  position: BoardPosition;
}

export interface TurnResult {
  success: boolean;
  error?: string;
  newBoard: BoardState;
  updatedWarriors: WarriorInstance[];
  action?: GameAction;
  defeatedWarriors?: string[];
  damagedWarriors?: DamageInfo[];
}

export class TurnManager {
  /**
   * Get the next player's turn
   */
  static getNextPlayer(currentPlayer: PlayerId): PlayerId {
    return currentPlayer === 'player1' ? 'player2' : 'player1';
  }

  /**
   * Validate that it's the correct player's turn
   */
  static validatePlayerTurn(
    playerId: PlayerId,
    currentTurn: PlayerId
  ): boolean {
    return playerId === currentTurn;
  }

  /**
   * Validate that the warrior belongs to the player
   */
  static validateWarriorOwnership(
    warrior: WarriorInstance,
    playerId: PlayerId
  ): boolean {
    return warrior.ownerId === playerId;
  }

  /**
   * Execute a move action
   */
  static executeMove(
    warrior: WarriorInstance,
    to: BoardPosition,
    board: BoardState,
    allWarriors: WarriorInstance[]
  ): TurnResult {
    if (!warrior.position) {
      return {
        success: false,
        error: 'Warrior has no position',
        newBoard: board,
        updatedWarriors: allWarriors,
      };
    }

    const validPositions = BoardManager.getValidMovePositions(board, warrior.position);
    const isValidMove = validPositions.some(
      pos => pos.row === to.row && pos.col === to.col
    );

    if (!isValidMove) {
      return {
        success: false,
        error: 'Invalid move position',
        newBoard: board,
        updatedWarriors: allWarriors,
      };
    }

    // Determine direction
    const dx = to.col - warrior.position.col;
    const dy = to.row - warrior.position.row;
    const direction = this.getDirectionFromDelta(dx, dy);

    const newBoard = BoardManager.moveWarrior(board, warrior.position, to);
    let updatedWarriors = allWarriors.map(w => {
      if (w.instanceId === warrior.instanceId) {
        return { ...w, position: to };
      }
      return w;
    });

    // Increment cooldowns
    updatedWarriors = CombatResolver.incrementCooldowns(warrior, updatedWarriors);

    const action: MoveAction = {
      type: 'move',
      playerId: warrior.ownerId,
      warriorInstanceId: warrior.instanceId,
      timestamp: Date.now(),
      direction,
      from: warrior.position,
      to,
    };

    return {
      success: true,
      newBoard,
      updatedWarriors,
      action,
    };
  }

  /**
   * Execute a dash action
   */
  static executeDash(
    warrior: WarriorInstance,
    to: BoardPosition,
    board: BoardState,
    allWarriors: WarriorInstance[]
  ): TurnResult {
    if (!warrior.position) {
      return {
        success: false,
        error: 'Warrior has no position',
        newBoard: board,
        updatedWarriors: allWarriors,
      };
    }

    // Check if dash is available
    if (warrior.definition.stats.dashFrequency <= 0) {
      return {
        success: false,
        error: 'Warrior cannot dash',
        newBoard: board,
        updatedWarriors: allWarriors,
      };
    }

    if (warrior.dashCooldown < warrior.definition.stats.dashFrequency) {
      return {
        success: false,
        error: 'Dash not charged',
        newBoard: board,
        updatedWarriors: allWarriors,
      };
    }

    const validPositions = BoardManager.getValidDashPositions(board, warrior.position);
    const isValidDash = validPositions.some(
      pos => pos.row === to.row && pos.col === to.col
    );

    if (!isValidDash) {
      return {
        success: false,
        error: 'Invalid dash position',
        newBoard: board,
        updatedWarriors: allWarriors,
      };
    }

    const dx = to.col - warrior.position.col;
    const dy = to.row - warrior.position.row;
    const direction = this.getCardinalDirectionFromDelta(dx, dy);

    const newBoard = BoardManager.moveWarrior(board, warrior.position, to);
    let updatedWarriors = allWarriors.map(w => {
      if (w.instanceId === warrior.instanceId) {
        return {
          ...w,
          position: to,
          dashCooldown: 0, // Reset dash cooldown
        };
      }
      return w;
    });

    // Increment other cooldowns (not dash since it was just used)
    updatedWarriors = updatedWarriors.map(w => {
      if (w.instanceId === warrior.instanceId) {
        return {
          ...w,
          attackCooldown: w.attackCooldown + 1,
          healCooldown:
            w.definition.stats.healingPower > 0 ? w.healCooldown + 1 : 0,
        };
      }
      return w;
    });

    const action: DashAction = {
      type: 'dash',
      playerId: warrior.ownerId,
      warriorInstanceId: warrior.instanceId,
      timestamp: Date.now(),
      direction,
      from: warrior.position,
      to,
    };

    return {
      success: true,
      newBoard,
      updatedWarriors,
      action,
    };
  }

  /**
   * Execute an attack action
   */
  static executeAttack(
    warrior: WarriorInstance,
    board: BoardState,
    allWarriors: WarriorInstance[]
  ): TurnResult {
    if (!warrior.position) {
      return {
        success: false,
        error: 'Warrior has no position',
        newBoard: board,
        updatedWarriors: allWarriors,
      };
    }

    if (warrior.attackCooldown < warrior.definition.stats.attackFrequency) {
      return {
        success: false,
        error: 'Attack not charged',
        newBoard: board,
        updatedWarriors: allWarriors,
      };
    }

    // Get original positions before combat (in case warriors are defeated)
    const originalPositions = new Map<string, BoardPosition>();
    for (const w of allWarriors) {
      if (w.position) {
        originalPositions.set(w.instanceId, w.position);
      }
    }

    const combatResult = CombatResolver.resolveAttack(warrior, allWarriors, board);

    // Remove defeated warriors from board using their original positions
    let newBoard = board;
    for (const defeatedId of combatResult.defeatedWarriors) {
      const originalPos = originalPositions.get(defeatedId);
      if (originalPos) {
        newBoard = BoardManager.removeWarrior(newBoard, originalPos);
      }
    }

    // Build damage record
    const damageDealt: Record<string, number> = {};
    for (const result of combatResult.damageResults) {
      damageDealt[result.targetId] = result.damage;
    }

    const isPlayer2 = warrior.ownerId === 'player2';
    const targetPositions = BoardManager.getAttackTargetPositions(
      warrior.position,
      warrior.definition.attackPattern,
      isPlayer2
    );

    const action: AttackAction = {
      type: 'attack',
      playerId: warrior.ownerId,
      warriorInstanceId: warrior.instanceId,
      timestamp: Date.now(),
      targetPositions,
      damageDealt,
    };

    // Increment cooldowns (attack cooldown was reset in CombatResolver)
    let updatedWarriors = combatResult.updatedWarriors.map(w => {
      if (w.instanceId === warrior.instanceId) {
        return {
          ...w,
          healCooldown:
            w.definition.stats.healingPower > 0 ? w.healCooldown + 1 : 0,
          dashCooldown:
            w.definition.stats.dashFrequency > 0 ? w.dashCooldown + 1 : 0,
        };
      }
      return w;
    });

    // Build damagedWarriors for warriors that were hit but not defeated
    const damagedWarriors: DamageInfo[] = combatResult.damageResults
      .filter(result => !result.isDefeated)
      .map(result => {
        const pos = originalPositions.get(result.targetId);
        return {
          targetId: result.targetId,
          damage: result.damage,
          position: pos!,
        };
      })
      .filter(info => info.position !== undefined);

    return {
      success: true,
      newBoard,
      updatedWarriors,
      action,
      defeatedWarriors: combatResult.defeatedWarriors,
      damagedWarriors,
    };
  }

  /**
   * Execute a heal action
   */
  static executeHeal(
    warrior: WarriorInstance,
    allWarriors: WarriorInstance[],
    board: BoardState
  ): TurnResult {
    if (!warrior.position) {
      return {
        success: false,
        error: 'Warrior has no position',
        newBoard: board,
        updatedWarriors: allWarriors,
      };
    }

    if (warrior.definition.stats.healingPower <= 0) {
      return {
        success: false,
        error: 'Warrior cannot heal',
        newBoard: board,
        updatedWarriors: allWarriors,
      };
    }

    const healResult = CombatResolver.resolveHeal(warrior, allWarriors);

    const healedWarriors: Record<string, number> = {};
    for (const result of healResult.healResults) {
      healedWarriors[result.targetId] = result.healAmount;
    }

    const action: HealAction = {
      type: 'heal',
      playerId: warrior.ownerId,
      warriorInstanceId: warrior.instanceId,
      timestamp: Date.now(),
      healedWarriors,
    };

    // Increment cooldowns (healCooldown was reset in CombatResolver, don't increment it)
    const updatedWarriors = healResult.updatedWarriors.map(w => {
      if (w.instanceId === warrior.instanceId) {
        return {
          ...w,
          attackCooldown: w.attackCooldown + 1,
          // healCooldown is already 0 from resolveHeal, don't increment
          dashCooldown:
            w.definition.stats.dashFrequency > 0 ? w.dashCooldown + 1 : 0,
        };
      }
      return w;
    });

    return {
      success: true,
      newBoard: board,
      updatedWarriors,
      action,
    };
  }

  private static getDirectionFromDelta(
    dx: number,
    dy: number
  ): import('@/types').Direction {
    if (dx === 0 && dy === -1) return 'n';
    if (dx === 1 && dy === -1) return 'ne';
    if (dx === 1 && dy === 0) return 'e';
    if (dx === 1 && dy === 1) return 'se';
    if (dx === 0 && dy === 1) return 's';
    if (dx === -1 && dy === 1) return 'sw';
    if (dx === -1 && dy === 0) return 'w';
    if (dx === -1 && dy === -1) return 'nw';
    return 'n'; // Default
  }

  private static getCardinalDirectionFromDelta(
    dx: number,
    dy: number
  ): import('@/types').CardinalDirection {
    if (dy < 0) return 'n';
    if (dx > 0) return 'e';
    if (dy > 0) return 's';
    return 'w';
  }
}
